import PageShell from "@/components/feature/PageShell";
import ScrollReveal from "@/components/feature/ScrollReveal";

const sections = [
  {
    num: "1",
    title: "Introduction",
    content:
      'These Terms and Conditions ("Terms") govern your use of the website and services provided by Presuni Ltd. By accessing or using our website or services, you agree to be bound by these Terms. If you do not agree with these Terms, you must not use our services.',
  },
  {
    num: "2",
    title: "Use of Our Services",
    bullets: [
      "Use our website and services only for lawful purposes",
      "Not misuse or attempt to disrupt our systems or security",
      "Provide accurate and up-to-date information when required",
    ],
    extra:
      "We reserve the right to suspend or terminate access if these Terms are violated.",
  },
  {
    num: "3",
    title: "Intellectual Property",
    content:
      "All content on our website, including text, graphics, logos, and software, is the property of Presuni Ltd or its licensors and is protected by intellectual property laws. You may not reproduce, distribute, or exploit any content without prior written consent.",
  },
  {
    num: "4",
    title: "Products and Services",
    bullets: [
      "We aim to ensure that all descriptions of our products or services are accurate. However, we do not guarantee that all information is complete, current, or error-free.",
      "We reserve the right to modify or discontinue services at any time",
      "We reserve the right to refuse or cancel orders where necessary",
    ],
  },
  {
    num: "5",
    title: "Pricing and Payment",
    bullets: [
      "All prices are listed in GBP (GBP) unless otherwise stated",
      "We reserve the right to change prices at any time",
      "Payment must be made in full before goods or services are provided, unless agreed otherwise",
    ],
  },
  {
    num: "6",
    title: "Limitation of Liability",
    bullets: [
      "Any indirect or consequential loss",
      "Loss of profits, revenue, or data",
      "Any damages arising from the use or inability to use our services",
    ],
    extra:
      "Nothing in these Terms excludes liability for death or personal injury caused by negligence or fraud.",
  },
  {
    num: "7",
    title: "Warranties and Disclaimers",
    bullets: [
      'Our services are provided "as is" and "as available" without warranties of any kind, either express or implied.',
      "We do not guarantee that the website will always be available or error-free",
      "We do not guarantee that the content is accurate or reliable at all times",
    ],
  },
  {
    num: "8",
    title: "Third-Party Links",
    content:
      "Our website may include links to third-party websites. We are not responsible for their content, terms, or privacy practices.",
  },
  {
    num: "9",
    title: "Termination",
    content:
      "We may terminate or suspend your access to our services immediately, without notice, if you breach these Terms.",
  },
  {
    num: "10",
    title: "Governing Law",
    content:
      "These Terms are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.",
  },
  {
    num: "11",
    title: "Changes to These Terms",
    content:
      "We may update these Terms from time to time. Continued use of our services after changes are posted constitutes acceptance of the updated Terms.",
  },
  {
    num: "12",
    title: "Contact Information",
    bullets: [
      "10 Winrush Close, Dudley, DY3 2NE, United Kingdom",
      "Email: info@presuni.co.uk",
    ],
  },
];

export default function Terms() {
  return (
    <PageShell>
      <section className="w-full pt-28 md:pt-36 pb-16 md:pb-24 bg-gray-50 dark:bg-dark-card">
        <div className="px-4 md:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 dark:text-white mb-2">
                Terms &amp; Conditions
              </h1>
              <p className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-1">
                Presuni Ltd
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1">
                Company Number: 05745823
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-8">
                Registered Address: 10 Winrush Close, DY3 2NE, Dudley, West Midlands, United Kingdom
              </p>

              <div className="space-y-8">
                {sections.map((s) => (
                  <div
                    key={s.num}
                    className="p-6 md:p-8 rounded-2xl bg-white dark:bg-dark-bg border border-gray-100 dark:border-dark-border"
                  >
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                      {s.num}. {s.title}
                    </h2>
                    {s.content && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                        {s.content}
                      </p>
                    )}
                    {s.bullets && (
                      <ul className="space-y-2">
                        {s.bullets.map((b) => (
                          <li key={b} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                            <i className="ri-checkbox-blank-circle-fill text-brand-500 text-[6px] mt-2 flex-shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.items && (
                      <div className="space-y-4">
                        {s.items.map((item) => (
                          <div key={item.sub}>
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              {item.sub}
                            </p>
                            <ul className="space-y-2">
                              {item.bullets.map((b) => (
                                <li key={b} className="flex items-start gap-2.5 text-sm text-gray-500 dark:text-gray-400">
                                  <i className="ri-checkbox-blank-circle-fill text-brand-500 text-[6px] mt-2 flex-shrink-0" />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                    {s.extra && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mt-3">
                        {s.extra}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 mt-8">
                Last Updated: 30/04/2026
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PageShell>
  );
}
